import { useAuth } from './useAuth'
import { useGroups } from './useGroups'
import { useGroupsEvents } from './useGroupsEvents'
import { useEventGroupScope } from './useEventGroupScope'
import { useFriendships } from './useFriendships'
import { useFriendIds } from './useFriendIds'
import { useFriendEventInterests } from './useFriendEventInterests'
import { useUserProfilesWithLoading } from './useUserProfiles'
import { useMergedSelfProfile } from './useMergedSelfProfile'
import { useFacepileData } from './useFacepileData'
import type { ClassifiedError } from '../lib/errors'
import type { EventAttendanceStatus, Group, GroupEvent } from '../types'
import type { FacepileBucket } from './useFacepileData'
import type { UserProfile } from '../types'
import type { FriendEventInterestBuckets } from './useFriendEventInterests'

export interface SocialEventData {
  facepileData: Map<string, FacepileBucket>
  mergedProfiles: Record<string, UserProfile>
  currentUserId: string | null
  /** Explicit friend IDs — use for friendship checks (e.g. "Friends" badge). */
  explicitFriendIds: string[]
  /** Explicit friends plus group co-members — whose interests the facepiles draw from. */
  friendIds: string[]
  /** User's groups — exposed for pages that need it for non-facepile logic. */
  groups: Group[]
  /** All group-event links — exposed for pages that derive groupLinkedEventIds etc. */
  allGroupEvents: GroupEvent[]
  /** Raw friend interest map — exposed for pages that need event-level friend counts. */
  friendInterests: Map<string, FriendEventInterestBuckets>
  /** True while groups or groupEvents are still loading. */
  socialLoading: boolean
  /** True until every peer's interests and profile have delivered — gate anything ordered by them. */
  peersPending: boolean
  /** Combined error from group/groupEvents subscriptions, if any. */
  socialError: ClassifiedError | null
}

/**
 * Bundles all peer-interest infrastructure: groups, friends, facepiles, profiles.
 * Pass in the current user's own event statuses (from useUserEventInterests) so
 * the facepile reflects their own going/interested status alongside friends.
 *
 * Pages still call:
 *   - useUserEventInterests — for myStatuses (passed here) + setEventInterest
 *   - useGuestGuard        — for the interest-button guest prompt
 *   - useRegionalEvents / useEventsByIds — for the event list
 */
export function useSocialEventData(myStatuses: Map<string, EventAttendanceStatus>): SocialEventData {
  const { user, signedInUid } = useAuth()

  const { groups, groupIds, loading: groupsLoading, error: groupsError } = useGroups(user?.uid ?? null)
  const { data: allGroupEvents, loading: groupEventsLoading } = useGroupsEvents(groupIds)
  const eventScope = useEventGroupScope(groups, allGroupEvents)

  const { friendIds: explicitFriendIds, friendIdSet: explicitFriendIdSet } = useFriendships(signedInUid)
  const friendIds = useFriendIds(user ?? null, groups, explicitFriendIds)

  const { map: friendInterests, pending: interestsPending } = useFriendEventInterests(friendIds)
  const { profiles: friendProfiles, loading: profilesPending } = useUserProfilesWithLoading(user ? [user.uid, ...friendIds] : friendIds)
  const mergedProfiles = useMergedSelfProfile(user ?? null, friendProfiles)

  const facepileData = useFacepileData(
    friendInterests,
    myStatuses,
    user?.uid ?? null,
    eventScope,
    explicitFriendIdSet,
  )

  return {
    facepileData,
    mergedProfiles,
    currentUserId: user?.uid ?? null,
    explicitFriendIds,
    friendIds,
    groups,
    allGroupEvents,
    friendInterests,
    socialLoading: groupsLoading || groupEventsLoading,
    peersPending: interestsPending || profilesPending,
    socialError: groupsError,
  }
}
